export interface RelativeTimeOptions {
  granularity?: 'seconds' | 'minutes' | 'hours'
  nowText?: string
  nowThreshold?: number
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const DATE_LOCALE = 'en-GB'

export type DateFormat = 'dd/MM/yy' | 'dd/MM/yyyy' | 'dd MMM' | 'dd MMM yyyy' | 'long'
export type DateTimeFormat = 'dd/MM/yy HH:mm' | 'short' | 'full'

export function formatDate(value: string | Date, format: DateFormat = 'dd/MM/yy'): string {
  const date = value instanceof Date ? value : parseInputDate(value)
  let options: Intl.DateTimeFormatOptions

  switch (format) {
    case 'dd/MM/yy':
      options = { day: '2-digit', month: '2-digit', year: '2-digit' }
      break
    case 'dd/MM/yyyy':
      options = { day: '2-digit', month: '2-digit', year: 'numeric' }
      break
    case 'dd MMM':
      options = { day: 'numeric', month: 'short' }
      break
    case 'dd MMM yyyy':
      options = { day: 'numeric', month: 'short', year: 'numeric' }
      break
    case 'long':
      options = { day: 'numeric', month: 'long', year: 'numeric' }
      break
  }

  return date.toLocaleDateString(DATE_LOCALE, options)
}

export function parseInputDate(value: string | Date): Date {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }

  const match = DATE_ONLY_PATTERN.exec(value)
  if (match) {
    const [, year, month, day] = match
    return new Date(Number(year), Number(month) - 1, Number(day))
  }

  return new Date(value)
}

export function formatRelativeTime(
  timestamp: string | Date,
  options: RelativeTimeOptions = {},
): string {
  const {
    granularity = 'minutes',
    nowText = 'Just now',
    nowThreshold = granularity === 'seconds' ? 5 : 1,
  } = options

  if (!timestamp) return nowText

  const now = new Date()
  const time = timestamp instanceof Date ? timestamp : new Date(timestamp)
  const diffInMs = now.getTime() - time.getTime()

  if (granularity === 'seconds') {
    const diffInSeconds = Math.floor(diffInMs / 1000)

    if (diffInSeconds < nowThreshold) return nowText
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    return formatShortTime(time)
  }

  if (granularity === 'hours') {
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))

    if (diffInHours < 1) return nowText
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`
    return formatDate(time)
  }

  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))

  if (diffInMinutes < nowThreshold) return nowText
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
  return formatDate(time)
}

export function formatInputDate(value: string | Date): string {
  const date = parseInputDate(value)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function formatShortTime(value: string | Date, includeSeconds = false): string {
  const date = value instanceof Date ? value : new Date(value)
  return date.toLocaleTimeString(DATE_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
  })
}

export function formatDateTime(
  value: string | Date,
  format: DateTimeFormat = 'short',
): string {
  const date = value instanceof Date ? value : new Date(value)
  let options: Intl.DateTimeFormatOptions

  switch (format) {
    case 'dd/MM/yy HH:mm':
      options = {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }
      break
    case 'full':
      options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }
      break
    case 'short':
      options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
      break
  }

  return date.toLocaleString(DATE_LOCALE, options)
}

export function isSameDay(date1: string | Date, date2: string | Date): boolean {
  const d1 = date1 instanceof Date ? date1 : new Date(date1)
  const d2 = date2 instanceof Date ? date2 : new Date(date2)

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  )
}
